import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../App';

function formatTs(unixSec) {
  return new Date(unixSec * 1000).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' });
}

// ── Login gate ────────────────────────────────────────────────────────────────
function LoginGate({ onSuccess }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/admin/votes`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) {
        onSuccess(password, data.votes || [], data.tampered || [], data.suspicious || []);
      } else {
        setError(data.error || 'தவறான கடவுச்சொல் | Invalid password');
      }
    } catch {
      setError('Network error — is Flask running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="center-screen" style={{ background: 'var(--bg)' }}>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏛️</div>
          <h2 className="auth-title">நிர்வாகி உள்நுழைவு</h2>
          <h3 style={{ fontSize: '1rem', color: 'var(--text-sub)', fontWeight: 500 }}>Admin Access</h3>
        </div>
        <p className="auth-subtitle">
          Enter the administrator password to access the vote log,
          blockchain audit, and tampered vote records.
        </p>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">கடவுச்சொல் | Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Admin password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              required
            />
          </div>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 0 }}>
              <span>⚠️</span><span>{error}</span>
            </div>
          )}
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'சரிபார்க்கிறது… Verifying…' : '🔐 Login'}
          </button>
        </form>
        <button className="link-btn" onClick={() => navigate('/')}>← முகப்பு | Back to Home</button>
      </div>
    </div>
  );
}

// ── Vote Log ──────────────────────────────────────────────────────────────────
function VoteLog({ votes, onRefresh, loading }) {
  return (
    <section className="admin-section">
      <div className="section-header">
        <div>
          <h3>வாக்கு பதிவேடு | Vote Log</h3>
          <p className="section-desc" style={{ marginBottom: 0 }}>
            All recorded votes with blockchain block hashes.
          </p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>
      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>வேட்பாளர் | Candidate</th>
              <th>கட்சி | Party</th>
              <th>நேரம் | Timestamp</th>
              <th>Block Hash</th>
            </tr>
          </thead>
          <tbody>
            {votes.length === 0
              ? <tr><td colSpan="5" className="empty-cell">வாக்குகள் இல்லை — No votes recorded yet.</td></tr>
              : votes.map(v => (
                  <tr key={v.id}>
                    <td><strong>{v.id}</strong></td>
                    <td style={{ fontWeight: 600, color: 'var(--tnec-navy)' }}>{v.candidate}</td>
                    <td><span className="badge badge-navy">{v.party || '—'}</span></td>
                    <td>{formatTs(v.timestamp)}</td>
                    <td className="hash-cell" title={v.block_hash}>
                      {v.block_hash.substring(0, 20)}…
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Tampered Log ──────────────────────────────────────────────────────────────
function TamperedLog({ tampered, onRefresh, loading }) {
  return (
    <section className="admin-section" style={{ borderColor: tampered.length > 0 ? 'var(--danger)' : 'var(--border)' }}>
      <div className="section-header">
        <div>
          <h3 style={{ color: tampered.length > 0 ? 'var(--danger)' : 'var(--tnec-navy)' }}>
            🚨 மோசடி வாக்குகள் | Tampered Vote Attempts
          </h3>
          <p className="section-desc" style={{ marginBottom: 0, color: tampered.length > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
            {tampered.length > 0
              ? `${tampered.length} double-vote attempt(s) detected and logged`
              : 'No tampered vote attempts detected — election integrity maintained.'}
          </p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {tampered.length > 0 && (
        <div className="alert alert-error" style={{ marginBottom: 12 }}>
          <span>🚨</span>
          <strong>BLOCKCHAIN ALERT:</strong>&nbsp;The following entries represent attempted fraud.
          Each is permanently recorded in the blockchain tamper log.
        </div>
      )}

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>கைரேகை Slot | Voter Slot</th>
              <th>முயற்சி | Attempted For</th>
              <th>அசல் வாக்கு | Originally Voted</th>
              <th>நேரம் | Time</th>
              <th>நிலை | Status</th>
            </tr>
          </thead>
          <tbody>
            {tampered.length === 0
              ? <tr><td colSpan="6" className="empty-cell" style={{ color: 'var(--success)' }}>
                  ✅ மோசடி இல்லை — No tampered attempts recorded.
                </td></tr>
              : tampered.map(t => (
                  <tr key={t.id} className="row-tampered">
                    <td><strong>{t.id}</strong></td>
                    <td style={{ fontWeight: 700 }}>Slot #{t.voter_id}</td>
                    <td>{t.attempted_candidate}</td>
                    <td style={{ fontWeight: 600, color: 'var(--tnec-navy)' }}>{t.original_candidate}</td>
                    <td>{formatTs(t.timestamp)}</td>
                    <td><span className="badge badge-danger">TAMPERED</span></td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Blockchain Audit ──────────────────────────────────────────────────────────
function BlockchainAudit() {
  const [chain,   setChain]   = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/verify-chain`);
      const data = await res.json();
      setChain(data);
    } catch {
      setChain({ valid: false, blocks: [], total: 0, tampered_attempts: 0, error: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <div>
          <h3>⛓️ Blockchain ஒருமைப்பாடு | Blockchain Integrity</h3>
          <p className="section-desc" style={{ marginBottom: 0 }}>
            Recompute all SHA-256 block hashes to verify the vote ledger has not been altered.
          </p>
        </div>
        <button className="btn btn-sm btn-primary" onClick={verify} disabled={loading}>
          {loading ? 'சரிபார்க்கிறது…' : '🔍 Verify Chain'}
        </button>
      </div>

      {chain && (
        <>
          <div className={`chain-status ${chain.valid ? 'chain-valid' : 'chain-invalid'}`}>
            {chain.valid
              ? `✓  சங்கிலி சரியானது — Chain Intact — ${chain.total} block${chain.total !== 1 ? 's' : ''} verified`
              : `✗  சங்கிலி திருடப்பட்டது — Chain TAMPERED — Integrity breach detected!`}
          </div>
          {chain.tampered_attempts > 0 && (
            <div className="alert alert-error" style={{ marginBottom: 12 }}>
              <span>🚨</span>
              <strong>{chain.tampered_attempts}</strong> double-vote attempt(s) recorded in the tamper log.
            </div>
          )}
          {chain.blocks?.length > 0 && (
            <div className="table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Candidate</th>
                    <th>Party</th>
                    <th>Timestamp</th>
                    <th>Hash (truncated)</th>
                    <th>Valid</th>
                  </tr>
                </thead>
                <tbody>
                  {chain.blocks.map(b => (
                    <tr key={b.block} className={b.valid ? '' : 'row-invalid'}>
                      <td><strong>#{b.block}</strong></td>
                      <td style={{ fontWeight: 600, color: 'var(--tnec-navy)' }}>{b.candidate}</td>
                      <td><span className="badge badge-navy">{b.party || '—'}</span></td>
                      <td>{formatTs(b.timestamp)}</td>
                      <td className="hash-cell" title={b.full_hash}>{b.hash}</td>
                      <td style={{ fontWeight: 800, color: b.valid ? 'var(--success)' : 'var(--danger)' }}>
                        {b.valid ? '✓' : '✗ TAMPERED'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── Suspicious Attempts Log ───────────────────────────────────────────────────
function SuspiciousLog({ suspicious, onRefresh, loading }) {
  return (
    <section className="admin-section" style={{ borderColor: suspicious.length > 0 ? 'var(--tnec-orange)' : 'var(--border)' }}>
      <div className="section-header">
        <div>
          <h3 style={{ color: suspicious.length > 0 ? 'var(--tnec-orange)' : 'var(--tnec-navy)' }}>
            ⚠️ பதிவு செய்யப்படாத கைரேகைகள் | Unregistered Fingerprint Attempts
          </h3>
          <p className="section-desc" style={{ marginBottom: 0, color: suspicious.length > 0 ? 'var(--tnec-orange)' : 'var(--text-muted)' }}>
            {suspicious.length > 0
              ? `${suspicious.length} attempt(s) from fingerprint slots not in the voter registry — blocked and logged.`
              : 'No unregistered fingerprint attempts detected.'}
          </p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {suspicious.length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 12 }}>
          <span>⚠️</span>
          <strong>SECURITY ALERT:</strong>&nbsp;The following fingerprint slots attempted to vote but are not in the registered voter list.
          All attempts were blocked and recorded.
        </div>
      )}

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>கைரேகை Slot | Fingerprint Slot</th>
              <th>வேட்பாளர் | Attempted Candidate</th>
              <th>நேரம் | Time</th>
              <th>நிலை | Status</th>
            </tr>
          </thead>
          <tbody>
            {suspicious.length === 0
              ? <tr><td colSpan="5" className="empty-cell" style={{ color: 'var(--success)' }}>
                  ✅ சந்தேகமான முயற்சிகள் இல்லை — No unregistered attempts recorded.
                </td></tr>
              : suspicious.map(s => (
                  <tr key={s.id} style={{ background: 'rgba(232,112,26,0.05)' }}>
                    <td><strong>{s.id}</strong></td>
                    <td style={{ fontWeight: 700, color: 'var(--tnec-orange)' }}>Slot #{s.slot}</td>
                    <td>{s.attempted_candidate}</td>
                    <td>{formatTs(s.timestamp)}</td>
                    <td><span className="badge" style={{ background: 'var(--tnec-orange)', color: '#fff' }}>BLOCKED</span></td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Voter Enrollment ──────────────────────────────────────────────────────────
function EnrollmentForm({ password }) {
  const [slot,    setSlot]    = useState('');
  const [name,    setName]    = useState('');
  const [status,  setStatus]  = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/enroll`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password, slot: parseInt(slot, 10), voter_name: name }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus(`✓  Slot ${slot} assigned for "${name}". Trigger enrollment on the ESP32 device.`);
        setSlot('');
        setName('');
      } else {
        setStatus(`✗  Error: ${data.error}`);
      }
    } catch {
      setStatus('✗  Network error — check Flask connectivity.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="admin-section">
      <h3 style={{ marginBottom: 4 }}>👆 வாக்காளர் பதிவு | Voter Enrollment</h3>
      <p className="section-desc">
        Assign a fingerprint slot to a new voter. Then trigger enrollment directly on the
        ESP32 device by pressing the designated enrollment button sequence on the hardware.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">பெயர் | Voter Name</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. Rajan Kumar"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sensor Slot (1–127)</label>
            <input
              className="form-input"
              type="number"
              placeholder="e.g. 1"
              min="1"
              max="127"
              value={slot}
              onChange={e => setSlot(e.target.value)}
              required
            />
          </div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'அனுப்புகிறது…' : '+ Assign Enrollment Slot'}
        </button>
      </form>
      {status && (
        <div
          className={`alert ${status.startsWith('✓') ? 'alert-success' : 'alert-error'}`}
          style={{ marginTop: 14, marginBottom: 0 }}
        >
          {status.startsWith('✓') ? <span>✅</span> : <span>⚠️</span>}
          <span>{status}</span>
        </div>
      )}
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const navigate = useNavigate();
  const [password,   setPassword]   = useState(null);
  const [votes,      setVotes]      = useState([]);
  const [tampered,   setTampered]   = useState([]);
  const [suspicious, setSuspicious] = useState([]);
  const [vLoading,   setVLoading]   = useState(false);

  const handleLoginSuccess = (pwd, initialVotes, initialTampered, initialSuspicious) => {
    setPassword(pwd);
    setVotes(initialVotes);
    setTampered(initialTampered);
    setSuspicious(initialSuspicious);
  };

  const refreshLogs = useCallback(async () => {
    if (!password) return;
    setVLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/admin/votes`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) {
        setVotes(data.votes       || []);
        setTampered(data.tampered || []);
        setSuspicious(data.suspicious || []);
      }
    } catch {}
    setVLoading(false);
  }, [password]);

  if (!password) {
    return <LoginGate onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="screen">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button className="back-btn" onClick={() => navigate('/')}>← முகப்பு | Home</button>
        <div className="section-title" style={{ margin: 0, flex: 1 }}>
          நிர்வாகி பலகம் | Admin Control Panel
        </div>
        {tampered.length > 0 && (
          <span className="badge badge-danger" style={{ fontSize: '0.8rem', padding: '4px 12px' }}>
            🚨 {tampered.length} Tampered
          </span>
        )}
        {suspicious.length > 0 && (
          <span className="badge" style={{ fontSize: '0.8rem', padding: '4px 12px', background: 'var(--tnec-orange)', color: '#fff' }}>
            ⚠️ {suspicious.length} Blocked
          </span>
        )}
        <button className="btn btn-sm btn-secondary" onClick={() => setPassword(null)}>
          🔓 Logout
        </button>
      </div>

      {/* Alert banners */}
      {tampered.length > 0 && (
        <div className="alert alert-error" style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 12 }}>
          <span>🚨</span>
          <span>
            <strong>FRAUD ALERT:</strong>&nbsp;
            {tampered.length} tampered vote attempt{tampered.length !== 1 ? 's' : ''} detected.
            See the Tampered Votes section below.
          </span>
        </div>
      )}
      {suspicious.length > 0 && (
        <div className="alert alert-warning" style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 20 }}>
          <span>⚠️</span>
          <span>
            <strong>SECURITY ALERT:</strong>&nbsp;
            {suspicious.length} unregistered fingerprint slot{suspicious.length !== 1 ? 's' : ''} attempted to vote and {suspicious.length !== 1 ? 'were' : 'was'} blocked.
            See the Blocked Attempts section below.
          </span>
        </div>
      )}

      <VoteLog        votes={votes}           onRefresh={refreshLogs} loading={vLoading} />
      <TamperedLog    tampered={tampered}      onRefresh={refreshLogs} loading={vLoading} />
      <SuspiciousLog  suspicious={suspicious}  onRefresh={refreshLogs} loading={vLoading} />
      <BlockchainAudit />
      <EnrollmentForm password={password} />
    </div>
  );
}
